import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  getLoyaltyProgramLogs,
  getLoyaltyProgramLogsActive,
  getLoyaltyProgramLogsChart,
  getLoyaltyProgramLogsSummary,
} from "../api/AiAgentApi";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useNavigate } from "react-router-dom";

const ACTION_LABELS = {
  UPDATE_COUNT_COUPON: "Купоны",
  UPDATE_BONUS_BALANCE: "Бонусы",
  UPDATE_COUNT_HOOKAHS: "Кальяны",
  UPDATE_COUNT_USER_GAME: "Игры",
};

const ACTION_COLORS = {
  UPDATE_COUNT_COUPON: "secondary",
  UPDATE_BONUS_BALANCE: "primary",
  UPDATE_COUNT_HOOKAHS: "warning",
  UPDATE_COUNT_USER_GAME: "info",
};

const ACTION_CHART_COLORS = {
  UPDATE_COUNT_COUPON: "#7b1fa2",
  UPDATE_BONUS_BALANCE: "#1976d2",
  UPDATE_COUNT_HOOKAHS: "#ed6c02",
  UPDATE_COUNT_USER_GAME: "#0288d1",
};
const SERIES_CHART_COLORS = {
  UPDATE_COUNT_COUPON__IN: "#7b1fa2",
  UPDATE_COUNT_COUPON__OUT: "#ef4444",
  UPDATE_BONUS_BALANCE__IN: "#1e88e5",
  UPDATE_BONUS_BALANCE__OUT: "#f97316",
  UPDATE_COUNT_HOOKAHS__IN: "#fb8c00",
  UPDATE_COUNT_HOOKAHS__OUT: "#10b981",
  UPDATE_COUNT_USER_GAME__IN: "#039be5",
  UPDATE_COUNT_USER_GAME__OUT: "#ec4899",
};

const PERIOD_OPTIONS = [
  { value: "WEEK", label: "Недели" },
  { value: "MONTH", label: "Месяцы" },
  { value: "YEAR", label: "Годы" },
];

// Маппинг полей UI к константам сортировки на сервере
const SORT_MAPPING = {
  createdAt: "CREATED_AT",
  id: "ID",
  value: "VALUE",
  gameBonus: "GAME_BONUS",
};

const RANGE_ERROR_MESSAGE = "Неверный диапазон дат: поле 'От' не должно быть позже поля 'До'.";

const getErrorMessage = (error, fallbackText) => {
  if (error?.response?.status === 400) {
    return RANGE_ERROR_MESSAGE;
  }
  return error?.response?.data?.message || fallbackText;
};

const formatReadableNumber = (value) =>
  Number(value || 0).toLocaleString("ru-RU").replace(/\u00A0/g, " ");

const StatCard = ({ title, value, color = "text.primary" }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 220, flex: 1 }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
      {title}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 700, color }}>
      {formatReadableNumber(value)}
    </Typography>
  </Paper>
);

const buildSmoothPath = (coords) => {
  if (!coords || coords.length === 0) return "";
  if (coords.length === 1) return `M ${coords[0].x} ${coords[0].y}`;

  const tension = 1;
  let d = `M ${coords[0].x} ${coords[0].y}`;
  for (let i = 0; i < coords.length - 1; i++) {
    const p0 = i > 0 ? coords[i - 1] : coords[i];
    const p1 = coords[i];
    const p2 = coords[i + 1];
    const p3 = i !== coords.length - 2 ? coords[i + 2] : p2;
    const cp1x = p1.x + ((p2.x - p0.x) / 6) * tension;
    const cp1y = p1.y + ((p2.y - p0.y) / 6) * tension;
    const cp2x = p2.x - ((p3.x - p1.x) / 6) * tension;
    const cp2y = p2.y - ((p3.y - p1.y) / 6) * tension;
    d += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${p2.x} ${p2.y}`;
  }
  return d;
};

const formatChartDate = (date, period) => {
  if (!date) return "—";
  const value = dayjs(date);
  if (!value.isValid()) return "—";

  if (period === "DAY") return value.format("DD.MM HH:mm");
  if (period === "WEEK") return value.format("DD.MM.YYYY");
  if (period === "MONTH") return value.format("MM.YYYY");
  return value.format("YYYY");
};

const buildSeriesKey = (action, direction) => `${action}__${direction}`;
const parseSeriesKey = (key) => {
  const [action, direction] = String(key || "").split("__");
  return { action, direction };
};
const DIRECTION_LABELS = {
  IN: "Начисление",
  OUT: "Списание",
};
const formatLegendLabel = (action, direction) =>
  `${ACTION_LABELS[action] || action} ${DIRECTION_LABELS[direction] || direction || ""}`.trim();

const LoyaltyLogsChart = ({ period, chartData, loading, error }) => {
  const allSeries = useMemo(() => (Array.isArray(chartData?.series) ? chartData.series : []), [chartData]);
  const allSeriesKeys = useMemo(
    () => allSeries.map((serie) => buildSeriesKey(serie.action, serie.direction)),
    [allSeries]
  );
  const [visibleSeries, setVisibleSeries] = useState({});
  const [popup, setPopup] = useState(null);
  const popupOpen = Boolean(popup?.anchorEl);

  useEffect(() => {
    setVisibleSeries((prev) =>
      allSeriesKeys.reduce((acc, key) => {
        acc[key] = prev[key] !== false;
        return acc;
      }, {})
    );
  }, [allSeriesKeys]);

  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Загружаем график...
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="warning">{error}</Alert>;
  }

  if (allSeries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Нет данных для графика по выбранному периоду.
      </Typography>
    );
  }

  const points = Array.from(
    new Set(allSeries.flatMap((serie) => (serie?.points || []).map((point) => point?.date)).filter(Boolean))
  )
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
    .map((date) => {
      const entry = { date };
      allSeries.forEach((serie) => {
        const key = buildSeriesKey(serie.action, serie.direction);
        const valueByDate = new Map((serie?.points || []).map((point) => [point.date, Number(point?.value) || 0]));
        entry[key] = valueByDate.get(date) || 0;
      });
      return entry;
    });

  if (points.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Нет данных для графика по выбранному периоду.
      </Typography>
    );
  }

  const minWidth = 980;
  const pointStep = 110;
  const width = Math.max(minWidth, (points.length - 1) * pointStep + 160);
  const height = 340;
  const margin = { top: 20, right: 20, bottom: 72, left: 48 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const xInnerPadding = 26;
  const pointCount = points.length;
  const activeSeriesKeys = allSeriesKeys.filter((key) => visibleSeries[key]);

  const maxY = Math.max(
    1,
    ...points.flatMap((point) =>
      (activeSeriesKeys.length > 0 ? activeSeriesKeys : allSeriesKeys).map((key) => Number(point[key]) || 0)
    )
  );

  const xAt = (index) => {
    const usableWidth = Math.max(1, plotWidth - xInnerPadding * 2);
    if (pointCount <= 1) return margin.left + xInnerPadding + usableWidth / 2;
    return margin.left + xInnerPadding + (index / (pointCount - 1)) * usableWidth;
  };
  const yAt = (value) => margin.top + plotHeight - (Math.max(0, Math.min(Number(value) || 0, maxY)) / maxY) * plotHeight;
  const yTicks = Array.from(
    new Set([0, 0.25, 0.5, 0.75, 1].map((ratio) => Math.round(maxY * ratio)).sort((a, b) => a - b))
  );
  const xTickCount = pointCount <= 12 ? pointCount : 10;
  const xTickIndices = Array.from(
    new Set(
      Array.from({ length: xTickCount }, (_, i) =>
        Math.round((i * Math.max(0, pointCount - 1)) / Math.max(1, xTickCount - 1))
      )
    )
  );

  const seriesMeta = allSeriesKeys.reduce((acc, key) => {
    const { action, direction } = parseSeriesKey(key);
    const baseColor = ACTION_CHART_COLORS[action] || "#6b7280";
    const uniqueColor = SERIES_CHART_COLORS[key];
    acc[key] = {
      label: formatLegendLabel(action, direction),
      color: uniqueColor || baseColor,
    };
    return acc;
  }, {});
  const seriesCoords = activeSeriesKeys.reduce((acc, key) => {
    acc[key] = points.map((point, i) => ({
      x: xAt(i),
      y: yAt(point[key]),
      value: Number(point[key]) || 0,
      date: point.date,
    }));
    return acc;
  }, {});
  const toggleSeries = (key) => {
    setVisibleSeries((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const openPointPopup = (event, point, key, value) => {
    setPopup({
      anchorEl: event.currentTarget,
      point,
      key,
      value,
    });
  };
  const closePointPopup = () => setPopup(null);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
        {allSeriesKeys.map((key) => (
          <Chip
            key={key}
            size="small"
            variant="outlined"
            label={`${visibleSeries[key] ? "✓" : "○"} ${seriesMeta[key].label}`}
            onClick={() => toggleSeries(key)}
            sx={{
              cursor: "pointer",
              borderColor: seriesMeta[key].color,
              color: seriesMeta[key].color,
              bgcolor: visibleSeries[key] ? `${seriesMeta[key].color}20` : "transparent",
              opacity: visibleSeries[key] ? 1 : 0.5,
            }}
          />
        ))}
        <Typography variant="caption" color="text.secondary">
          Нажмите на линию, чтобы скрыть или показать ее.
        </Typography>
      </Stack>

      <Box sx={{ width: "100%", overflowX: "auto", pb: 0.5 }}>
        <svg width={width} height={height} role="img" aria-label="График операций по логам">
          {yTicks.map((tick) => {
            const y = yAt(tick);
            return (
              <g key={`y-${tick}`}>
                <line
                  x1={margin.left}
                  x2={margin.left + plotWidth}
                  y1={y}
                  y2={y}
                  stroke="rgba(255,255,255,0.12)"
                  strokeDasharray="3 3"
                />
                <text x={margin.left - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#8a8a8a">
                  {tick}
                </text>
              </g>
            );
          })}

          <line
            x1={margin.left}
            x2={margin.left}
            y1={margin.top}
            y2={margin.top + plotHeight}
            stroke="rgba(255,255,255,0.25)"
          />
          <line
            x1={margin.left}
            x2={margin.left + plotWidth}
            y1={margin.top + plotHeight}
            y2={margin.top + plotHeight}
            stroke="rgba(255,255,255,0.25)"
          />

          {activeSeriesKeys.map((key) => {
            const coords = seriesCoords[key];
            const d = buildSmoothPath(coords);
            return (
              <g key={key}>
                <path
                  d={d}
                  fill="none"
                  stroke={seriesMeta[key].color}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {coords.map((coord, i) => (
                  <circle
                    key={`${key}-${i}`}
                    cx={coord.x}
                    cy={coord.y}
                    r="3.5"
                    fill={seriesMeta[key].color}
                    style={{ cursor: "pointer" }}
                    onClick={(event) => openPointPopup(event, points[i], key, coord.value)}
                  >
                    <title>{`${seriesMeta[key].label}: ${coord.value}\n${coord.date}`}</title>
                  </circle>
                ))}
              </g>
            );
          })}

          {activeSeriesKeys.length === 0 && (
            <text
              x={margin.left + plotWidth / 2}
              y={margin.top + plotHeight / 2}
              textAnchor="middle"
              fontSize="14"
              fill="#8a8a8a"
            >
              Все линии скрыты. Включите хотя бы одну линию.
            </text>
          )}

          {xTickIndices.map((index) => {
            const x = xAt(index);
            return (
              <g key={`x-${index}`}>
                <line
                  x1={x}
                  x2={x}
                  y1={margin.top + plotHeight}
                  y2={margin.top + plotHeight + 6}
                  stroke="rgba(255,255,255,0.25)"
                />
                <text
                  x={x - 2}
                  y={margin.top + plotHeight + 18}
                  textAnchor="end"
                  transform={`rotate(-24 ${x - 2} ${margin.top + plotHeight + 18})`}
                  fontSize="11"
                  fill="#8a8a8a"
                >
                  {formatChartDate(points[index].date, period)}
                </text>
              </g>
            );
          })}
        </svg>
      </Box>

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Y: value
        </Typography>
        <Typography variant="caption" color="text.secondary">
          X: date
        </Typography>
      </Stack>

      <Popover
        open={popupOpen}
        anchorEl={popup?.anchorEl || null}
        onClose={closePointPopup}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{ sx: { p: 1.5, maxWidth: 420 } }}
      >
        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {seriesMeta[popup?.key]?.label || "Серия"}
          </Typography>
          <Typography variant="body2">
            Дата: {popup?.point?.date ? dayjs(popup.point.date).format("DD.MM.YYYY HH:mm:ss") : "—"}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Значение: {popup?.value ?? 0}
          </Typography>

          <Divider />
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {allSeriesKeys.map((key) => (
              <Chip
                key={key}
                size="small"
                variant="outlined"
                label={`${seriesMeta[key]?.label}: ${Number(popup?.point?.[key]) || 0}`}
                sx={{ borderColor: seriesMeta[key]?.color, color: seriesMeta[key]?.color }}
              />
            ))}
          </Stack>
        </Stack>
      </Popover>
    </Box>
  );
};

export const LoyaltyLogsPage = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logsError, setLogsError] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [page, setPage] = useState(0);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");

  const [chartPeriod, setChartPeriod] = useState("WEEK");
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState("");

  const [active, setActive] = useState(null);
  const [activeLoading, setActiveLoading] = useState(false);
  const [activeError, setActiveError] = useState("");

  const rowsPerPage = 20;

  const fromIso = useMemo(() => (fromDate ? dayjs(fromDate).toISOString() : null), [fromDate]);
  const toIso = useMemo(() => (toDate ? dayjs(toDate).toISOString() : null), [toDate]);

  const observer = useRef();
  const lastLogElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  // Дебаунс поиска
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
      setLogs([]);
      setHasMore(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Сброс при изменении фильтров или сортировки
  useEffect(() => {
    setPage(0);
    setLogs([]);
    setHasMore(true);
  }, [actionFilter, order, orderBy, fromIso, toIso]);

  const fetchLogs = useCallback(async () => {
    if (!hasMore && page !== 0) return;

    try {
      setLoading(true);
      setLogsError("");
      const params = {
        page,
        size: rowsPerPage,
        sortBy: SORT_MAPPING[orderBy] || "CREATED_AT",
        direction: order.toUpperCase(),
        action: actionFilter === "all" ? null : actionFilter,
        search: debouncedSearch,
        from: fromIso,
        to: toIso,
      };

      const response = await getLoyaltyProgramLogs(params);

      const newLogs = response?.content || [];

      setLogs((prev) => (page === 0 ? newLogs : [...prev, ...newLogs]));
      setHasMore(!response?.last && newLogs.length > 0);
    } catch (error) {
      console.error("Failed to fetch loyalty logs:", error);
      setLogsError(getErrorMessage(error, "Не удалось загрузить логи."));
    } finally {
      setLoading(false);
    }
  }, [page, orderBy, order, actionFilter, debouncedSearch, fromIso, toIso, hasMore]);

  const fetchSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      setSummaryError("");
      const data = await getLoyaltyProgramLogsSummary();
      setSummary(data || null);
    } catch (error) {
      console.error("Failed to fetch logs summary:", error);
      setSummaryError(getErrorMessage(error, "Не удалось загрузить сводку."));
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchChart = useCallback(async () => {
    try {
      setChartLoading(true);
      setChartError("");
      const data = await getLoyaltyProgramLogsChart({ period: chartPeriod });
      setChartData(data || null);
    } catch (error) {
      console.error("Failed to fetch logs chart:", error);
      setChartError(getErrorMessage(error, "Не удалось загрузить данные графика."));
      setChartData(null);
    } finally {
      setChartLoading(false);
    }
  }, [chartPeriod]);

  const fetchActive = useCallback(async () => {
    try {
      setActiveLoading(true);
      setActiveError("");
      const data = await getLoyaltyProgramLogsActive();
      setActive(data || null);
    } catch (error) {
      console.error("Failed to fetch active loyalty balances:", error);
      setActiveError(getErrorMessage(error, "Не удалось загрузить активные остатки."));
      setActive(null);
    } finally {
      setActiveLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchSummary();
    fetchChart();
  }, [fetchSummary, fetchChart]);

  useEffect(() => {
    fetchActive();
  }, [fetchActive]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const formatDateTime = (date) => (date ? dayjs(date).format("DD.MM.YYYY HH:mm:ss") : "—");

  const renderUserInfo = (user) => {
    if (!user)
      return (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          —
        </Typography>
      );
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {[user.firstName, user.lastName].filter(Boolean).join(" ") || "Без имени"}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
          ID: {user.id}
        </Typography>
      </Box>
    );
  };

  const renderCouponDetails = (coupon) => {
    if (!coupon) return null;
    return (
      <Box sx={{ mt: 1, p: 1, bgcolor: "action.hover", borderRadius: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: "bold", color: "secondary.main" }}>
          КУПОН (ID: {coupon.id})
        </Typography>
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          <Typography variant="caption">
            Тип: {coupon.type?.name} ({coupon.type?.action})
          </Typography>
          <Typography variant="caption" sx={{ display: "block" }}>
            Описание: {coupon.type?.description || "—"}
          </Typography>
          <Typography variant="caption" sx={{ display: "block" }}>
            Создан: {formatDateTime(coupon.createdAt)}
          </Typography>
          <Typography variant="caption" sx={{ display: "block" }}>
            Использован: {formatDateTime(coupon.useAt)}
          </Typography>
          <Typography variant="caption" sx={{ display: "block" }}>
            Истекает: {formatDateTime(coupon.expiredAt)}
          </Typography>
        </Stack>
      </Box>
    );
  };

  const renderBonusDetails = (bonus) => {
    if (!bonus) return null;
    return (
      <Box sx={{ mt: 1, p: 1, bgcolor: "action.hover", borderRadius: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: "bold", color: "primary.main" }}>
          БОНУС (ID: {bonus.id})
        </Typography>
        <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
          Истекает: {formatDateTime(bonus.expiredAt)}
        </Typography>
      </Box>
    );
  };

  const renderGameDetails = (game) => {
    if (!game) return null;
    return (
      <Box sx={{ mt: 1, p: 1, bgcolor: "action.hover", borderRadius: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: "bold", color: "info.main" }}>
          ИГРА (ID: {game.id})
        </Typography>
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          <Typography variant="caption">
            Название: {game.game?.name} (ID: {game.game?.id})
          </Typography>
          <Typography variant="caption" sx={{ display: "block" }}>
            Бонусы: <b>{game.countBonus || 0}</b>
          </Typography>
          <Typography variant="caption" sx={{ display: "block" }}>
            Истекает: {formatDateTime(game.expiredAt)}
          </Typography>
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          Логи программы лояльности
        </Typography>
        <Button variant="outlined" onClick={() => navigate("/loyalty-logs/coupon-mismatch-users")}>
          Пользователи с расхождением купонов
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        Блоки «Сводка», «График» и «Активные» обновляются один раз в день в 04:00 по Москве (МСК).
      </Alert>

      <Paper sx={{ p: 2, mb: 3, boxShadow: 2 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Сводка
        </Typography>

        {summaryLoading ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : summaryError ? (
          <Alert severity="error">{summaryError}</Alert>
        ) : !summary ? (
          <Typography color="text.secondary">Нет данных для сводки.</Typography>
        ) : (
          <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
            <StatCard
              title="Купонов выдано системой"
              value={summary.couponsIssuedBySystem}
              color="success.main"
            />
            <StatCard
              title="Купонов выдано сотрудником"
              value={summary.couponsIssuedByEmployee}
              color="success.main"
            />
            <StatCard
              title="Купонов списано системой"
              value={summary.couponsSpentBySystem}
              color="error.main"
            />
            <StatCard
              title="Купонов списано сотрудником"
              value={summary.couponsSpentByEmployee}
              color="error.main"
            />
            <StatCard title="Кальянов добавили" value={summary.hookahsAdded} color="success.main" />
            <StatCard title="Кальянов списали" value={summary.hookahsSpent} color="error.main" />
            <StatCard title="Бонусов начислено" value={summary.bonusesIssued} color="success.main" />
            <StatCard title="Бонусов списано" value={summary.bonusesSpent} color="error.main" />
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 3, boxShadow: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          sx={{ mb: 1.5 }}
        >
          <Typography variant="h6">График</Typography>
          <ButtonGroup size="small" variant="outlined">
            {PERIOD_OPTIONS.map((option) => (
              <Button
                key={option.value}
                variant={chartPeriod === option.value ? "contained" : "outlined"}
                onClick={() => setChartPeriod(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
        </Stack>

        <LoyaltyLogsChart
          period={chartPeriod}
          chartData={chartData}
          loading={chartLoading}
          error={chartError}
        />
      </Paper>

      <Paper sx={{ p: 2, mb: 3, boxShadow: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="h6">Активные</Typography>
          <Button size="small" onClick={fetchActive} disabled={activeLoading}>
            Обновить
          </Button>
        </Stack>

        {activeLoading ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : activeError ? (
          <Alert severity="error">{activeError}</Alert>
        ) : !active ? (
          <Typography color="text.secondary">Нет данных по активным остаткам.</Typography>
        ) : (
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <StatCard title="Активные купоны" value={active.activeCoupons} />
            <StatCard title="Активные игры" value={active.activeGames} />
            <StatCard title="Активные бонусы" value={active.activeBonuses} />
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 3, boxShadow: 2 }}>
        <Stack direction="column" spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <TextField
              fullWidth
              size="small"
              label="Поиск"
              variant="outlined"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: "action.active", mr: 1 }} />,
              }}
            />
            <TextField
              select
              size="small"
              label="Тип действия"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: <FilterListIcon sx={{ color: "action.active", mr: 1 }} />,
              }}
            >
              <MenuItem value="all">Все действия</MenuItem>
              {Object.entries(ACTION_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="От"
              type="datetime-local"
              size="small"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="До"
              type="datetime-local"
              size="small"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Stack>
        </Stack>
      </Paper>

      {logsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {logsError}
        </Alert>
      )}

      <TableContainer
        component={Paper}
        sx={{ boxShadow: 3, borderRadius: 2, maxHeight: "calc(100vh - 340px)" }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === "createdAt" ? order : false}>
                <TableSortLabel
                  active={orderBy === "createdAt"}
                  direction={orderBy === "createdAt" ? order : "asc"}
                  onClick={() => handleRequestSort("createdAt")}
                >
                  Дата
                </TableSortLabel>
              </TableCell>
              <TableCell>Пользователь</TableCell>
              <TableCell>Сотрудник</TableCell>
              <TableCell sortDirection={orderBy === "action" ? order : false}>
                <TableSortLabel
                  active={orderBy === "action"}
                  direction={orderBy === "action" ? order : "asc"}
                  onClick={() => handleRequestSort("action")}
                >
                  Действие
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sortDirection={orderBy === "value" ? order : false}>
                <TableSortLabel
                  active={orderBy === "value"}
                  direction={orderBy === "value" ? order : "asc"}
                  onClick={() => handleRequestSort("value")}
                >
                  Значение
                </TableSortLabel>
              </TableCell>
              <TableCell>Информация о бонусах / купонах / играх</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log, index) => {
              const isLastElement = logs.length === index + 1;
              return (
                <TableRow
                  key={`${log.createdAt}-${index}`}
                  hover
                  ref={isLastElement ? lastLogElementRef : null}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(log.createdAt)}</TableCell>
                  <TableCell>{renderUserInfo(log.user)}</TableCell>
                  <TableCell>{renderUserInfo(log.employee)}</TableCell>
                  <TableCell>
                    <Chip
                      label={ACTION_LABELS[log.action] || log.action}
                      color={ACTION_COLORS[log.action] || "default"}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "bold",
                        color: log.value > 0 ? "success.main" : log.value < 0 ? "error.main" : "text.primary",
                      }}
                    >
                      {log.value > 0 ? `+${log.value}` : log.value}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 300 }}>
                    {renderCouponDetails(log.coupon)}
                    {renderBonusDetails(log.bonus)}
                    {renderGameDetails(log.game)}
                    {!log.coupon && !log.bonus && !log.game && (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {loading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <Typography variant="body1" color="text.secondary">
                    Логи не найдены
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
